/**
 * a visual-inspection pass (services/recipe/inspection_pass.py).
 *
 * An Inspection step of a visual-inspection operation can be ONE pass over the tubs: add one Inspection step for each pass the
 * operation needs (two for a first and a second pass, one for a single pass). The pass says what it is called, which units it takes,
 * and whether it is optional. A pass after the first follows an earlier pass (Depends on, or the operation's order); a step placed
 * after the passes (the supervisor's review) waits for every one of them. The server refuses anything it cannot work.
 *
 * A Visual Inspection step is one pass by itself, with no setting: the server writes it into the operation's stages, named as the step,
 * and states the pass it is as `inspection_pass` while the step's own payload carries none. Such a step reads as one inspection here;
 * a setting switched on is an override of what it is by itself.
 */
import React from "react";
import { Flex, Input, Select, Switch, Typography } from "antd";

const { Text } = Typography;

// where an author starts from; every word and choice can be changed afterwards
const PRESETS = [
  { value: "FIRST", label: "First pass", setting: { label: "1st visual", input: "all", optional: false } },
  { value: "SECOND", label: "Second pass", setting: { label: "2nd visual", input: "accepted", optional: false } },
  { value: "RE_INSPECTION", label: "Re-inspection", setting: { label: "Re-inspection", input: "flagged", optional: true } },
];
const UNITS = [
  { value: "all", label: "All the units" },
  { value: "accepted", label: "The units the earlier pass accepted" },
  { value: "flagged", label: "The units the earlier pass flagged" },
];
const LABEL_MAX = 40;

/** Whether the step is a pass by itself: the server states a pass the step's own payload does not set, and none is being set here. */
export const passByItself = (step) => !!step?.inspection_pass && !step?.payload?.inspection_pass && !step?.__pass_staged;

export default function InspectionPassField({ step, operation, readOnly, onChange }) {
  const byItself = passByItself(step);
  const pass = byItself ? null : step?.inspection_pass || null;
  const earlier = (operation?.children || []).filter((c) => c.inspection_pass && c.code !== step?.code);
  const set = (patch) => onChange({ inspection_pass: { ...pass, ...patch } });
  const start = () => onChange({ inspection_pass: { ...(earlier.length ? PRESETS[1] : PRESETS[0]).setting } });
  return (
    <div>
      <Flex align="center" gap={8}>
        <Switch size="small" checked={!!pass} disabled={readOnly} onChange={(on) => (on ? start() : onChange({ inspection_pass: null }))} />
        <Text style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>{pass ? "This step is one pass of the visual inspection" : byItself ? "This step is one inspection by itself" : "Not a pass"}</Text>
      </Flex>
      {byItself ? (
        <Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
          Named as the step, it takes {earlier.length ? "the units the earlier pass accepted" : "all the units"}. Switch on only to state its own name, units or whether it is optional.
        </Text>
      ) : null}
      {pass ? (
        <Flex vertical gap={6} style={{ marginTop: 8 }}>
          <Select size="small" placeholder="Start from…" disabled={readOnly} value={undefined} options={PRESETS.map((p) => ({ value: p.value, label: p.label }))}
            onChange={(v) => onChange({ inspection_pass: { ...PRESETS.find((p) => p.value === v).setting } })} />
          <Flex align="center" gap={8}>
            <Text style={{ width: 110, fontSize: "calc(12px * var(--dz-fs, 1))" }}>Name <span style={{ color: "#b3261e" }}>*</span></Text>
            <Input size="small" maxLength={LABEL_MAX} disabled={readOnly} value={pass.label || ""} onChange={(e) => set({ label: e.target.value })} />
          </Flex>
          <Flex align="center" gap={8}>
            <Text style={{ width: 110, fontSize: "calc(12px * var(--dz-fs, 1))" }}>Takes</Text>
            <Select size="small" style={{ flex: 1 }} disabled={readOnly} value={pass.input || "all"} options={UNITS.map((u) => ({ ...u, disabled: u.value !== "all" && !earlier.length }))}
              onChange={(v) => set({ input: v })} />
          </Flex>
          <Flex align="center" gap={8}>
            <Text style={{ width: 110, fontSize: "calc(12px * var(--dz-fs, 1))" }}>Optional</Text>
            <Switch size="small" checked={!!pass.optional} disabled={readOnly} onChange={(v) => set({ optional: v })} />
          </Flex>
          <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
            {earlier.length
              ? `Follows ${earlier.map((c) => c.code).join(", ")}. Make it depend on the earlier pass, so it opens once that pass is signed.`
              : "The first pass takes all the units. Add another Inspection step after it for a second pass."}
          </Text>
        </Flex>
      ) : null}
    </div>
  );
}
